// @ts-nocheck
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router'
import { useQuery } from '@apollo/client/react'
import { formatISO } from 'date-fns'
import showDate, { isSameDate } from 'helpers/date/showDate'
import getGroupUrl from 'helpers/getGroupUrl'

import { CameraIcon } from '@pleio/icons/camera'

import Button from 'js/components/Button'
import CoverImage from 'js/components/CoverImage'
import Flexer from 'js/components/Flexer/Flexer'
import { Col, Container, Row } from 'js/components/Grid'
import { H1, H4 } from 'js/components/Heading'
import HideVisually from 'js/components/HideVisually/HideVisually'
import ToggleTranslation from 'js/components/Item/components/ToggleTranslation'
import ItemActions from 'js/components/Item/ItemActions/ItemActions'
import ItemStatusTag from 'js/components/Item/ItemStatusTag'
import ItemSuggestedItems from 'js/components/Item/ItemSuggestedItems'
import ItemTags from 'js/components/ItemTags'
import Modal from 'js/components/Modal/Modal'
import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import Section from 'js/components/Section/Section'
import SocialShare from 'js/components/SocialShare/SocialShare'
import Text from 'js/components/Text/Text'
import TiptapView from 'js/components/Tiptap/TiptapView'
import { NotFound } from 'js/core/NotFound'
import groupActivitiesByDate from 'js/lib/helpers/groupActivitiesByDate'
import { useSiteStore } from 'js/lib/stores/SiteStore'
import ThemeProvider from 'js/theme/ThemeProvider'

import CommentList from '../components/CommentList'
import Document from '../components/Document'

import Activity from './components/Activity'
import ActivityActions from './components/ActivityActions'
import AttendeesActionsCard from './components/AttendeesActionsCard'
import EventDate from './components/EventDate'
import EventLink from './components/EventLink'
import EventLocation from './components/EventLocation'
import SendMessage from './components/SendMessage'
import SlotsForm from './components/SlotsForm'
import AttendeesModal from './EventAttendees/components/AttendeesModal'
import { EVENT_ITEM } from './EventItem.queries'
import { EVENT_ITEM_CHILD_DETAILS } from './EventItemWithDetailedChildren.queries'
import getRefetchQueries from './helpers/getRefetchQueries'
import styles from './Item.module.css'
import AddAttendee from './ItemActions/AddAttendee'
import InvitePeople from './ItemActions/InvitePeople/InvitePeople'

const Item = () => {
    const params = useParams()
    const { hash } = useLocation()
    const { guid, groupGuid } = params

    const { site } = useSiteStore()
    const {
        integratedVideocallEnabled,
        showCustomTagsInDetail,
        showTagsInDetail,
    } = site

    const { loading, data } = useQuery(EVENT_ITEM, {
        variables: {
            guid,
            groupGuid,
        },
        fetchPolicy: 'cache-and-network',
    })

    /**
     * Here we load the sub events under our main event group
     *
     * These details are loaded sequentially to offload the main query
     * Our main query above can finish quicker so the major part of the page can be rendered faster
     *
     * TODO: when we are inside a group its event already there is no need to retrieve these sub events
     */
    const { data: dataChildDetails } = useQuery(EVENT_ITEM_CHILD_DETAILS, {
        variables: {
            guid,
        },
        fetchPolicy: 'cache-and-network',
        skip: loading || !data,
    })

    const { t } = useTranslation()

    useEffect(() => {
        if (!loading && data?.entity) {
            setIsTranslated(!!data.entity.localTitle)
        }
    }, [loading, data])

    const [attendeesModal, setAttendeesModal] = useState(false)

    const [showInvitePeopleModal, setShowInvitePeopleModal] = useState(false)
    const toggleInvitePeopleModal = () =>
        setShowInvitePeopleModal(!showInvitePeopleModal)

    const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false)
    const toggleAddAttendeeModal = () =>
        setShowAddAttendeeModal(!showAddAttendeeModal)

    const [showSendMessageModal, setShowSendMessageModal] = useState(false)
    const toggleSendMessageModal = () =>
        setShowSendMessageModal(!showSendMessageModal)

    const onAfterDelete = () => `${getGroupUrl(params)}/events`

    const [showSlotsModal, setShowSlotsModal] = useState(false)
    const toggleSlotsModal = () => {
        setShowSlotsModal(!showSlotsModal)
    }

    const [isTranslated, setIsTranslated] = useState(null)

    if (loading) {
        return null
    }
    if (!loading && !data?.entity) {
        return <NotFound />
    }

    const { entity, viewer } = data
    const {
        localTitle,
        title,
        url,
        featured,
        parent,
        group,
        location,
        locationAddress,
        locationLink,
        hasChildren,
        children = [],
        statusPublished,
        canAttendWithEmail,
        startDate,
        endDate,
        source,
        localRichDescription,
        richDescription,
        tags,
        tagCategories,
        commentCount,
        suggestedItems,
        isTranslationEnabled,
        canEdit,
        videoCallEnabled,
        videoCallUrl,
    } = entity

    const hasTranslations = !!localTitle && isTranslationEnabled

    const translatedTitle = isTranslated ? localTitle : title
    const translatedRichDescription = isTranslated
        ? localRichDescription
        : richDescription

    const onEdit = `${getGroupUrl(params)}/events/edit/${guid}`
    const refetchQueries = getRefetchQueries(true, !!group)
    const containerTitle = group?.name
    const showLocation = !!location || !!locationAddress || !!locationLink

    let activitiesByDate,
        activitiesByHash,
        activeHashActivities,
        showActivityDateTabs

    if (hasChildren) {
        activitiesByDate = groupActivitiesByDate(
            dataChildDetails?.entity?.children || children,
        )

        activitiesByHash =
            hash &&
            activitiesByDate.find((el) => {
                return Object.keys(el)[0] === hash.substring(1)
            })

        activeHashActivities = activitiesByHash
            ? Object.values(activitiesByHash)?.at(0)
            : hasChildren
              ? Object.values(activitiesByDate?.at(0))?.at(0)
              : null

        // Show date tabs if there are one or moreactivities spread over multiple days
        showActivityDateTabs =
            activitiesByDate.length > 1 ||
            (activitiesByDate.length === 1 &&
                (!isSameDate(children[0].startDate, startDate) ||
                    !isSameDate(children[0].startDate, endDate)))
    }

    const isPublished = statusPublished === 'published'

    const canEditItem = canEdit && statusPublished !== 'archived'

    const canAddSubEvent = !parent && !entity?.rangeSettings?.type // Not an activity or repeating event

    const canAttendSubEvents =
        !parent &&
        (entity?.requireAttendParent
            ? ['accept', 'online'].includes(entity?.isAttending)
            : true)

    const hasSuggestedItems = suggestedItems?.length > 0

    return (
        <ThemeProvider bodyColor="#FFFFFF">
            <Document title={translatedTitle} containerTitle={containerTitle} />

            <Section
                className={styles.wrapper}
                backgroundColor="white"
                style={{ paddingTop: 0 }}
            >
                <Container className="EventCoverContainer">
                    <CoverImage featured={featured} />
                </Container>
                <Container>
                    <Row $spacing={20} style={{ marginTop: '32px' }}>
                        <Col mobileLandscape={1 / 2} tabletUp={2 / 3}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div>
                                    <H1 as={groupGuid ? 'h2' : 'h1'}>
                                        {translatedTitle}
                                        <ItemStatusTag
                                            entity={entity}
                                            style={{
                                                marginTop: '8px',
                                            }}
                                        />
                                    </H1>

                                    {parent && (
                                        <Text size="small">
                                            <HideVisually>
                                                {t(
                                                    'entity-event.sub-event-of',
                                                )}{' '}
                                            </HideVisually>
                                            <Link to={parent.url}>
                                                {parent.title}
                                            </Link>
                                        </Text>
                                    )}
                                </div>

                                <ItemActions
                                    showEditButton
                                    entity={entity}
                                    onEdit={onEdit}
                                    onAfterDelete={onAfterDelete}
                                    canDuplicate
                                    refetchQueries={refetchQueries}
                                    customOptions={[
                                        [
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.attendees',
                                                          ),
                                                          onClick: () =>
                                                              setAttendeesModal(
                                                                  'going',
                                                              ),
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit &&
                                            window.__SETTINGS__.eventExport
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.export-attendees',
                                                          ),
                                                          url: `/exporting/event/${guid}`,
                                                          download: true,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.guest-list',
                                                          ),
                                                          url: `${url}/guest-list`,
                                                          newWindow: true,
                                                      },
                                                  ]
                                                : []),
                                        ],
                                        [
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'global.send-message',
                                                          ),
                                                          onClick:
                                                              toggleSendMessageModal,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'global.invite-people',
                                                          ),
                                                          onClick:
                                                              toggleInvitePeopleModal,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit && canAttendWithEmail
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.add-attendee',
                                                          ),
                                                          onClick:
                                                              toggleAddAttendeeModal,
                                                      },
                                                  ]
                                                : []),
                                        ],
                                        [
                                            ...(canEditItem && hasChildren
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.activity-slots',
                                                          ),
                                                          onClick:
                                                              toggleSlotsModal,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEditItem && canAddSubEvent
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.create-activity',
                                                          ),
                                                          url: `${getGroupUrl(
                                                              params,
                                                          )}/events/add/${guid}`,
                                                      },
                                                  ]
                                                : []),
                                        ],
                                    ]}
                                    style={{ marginLeft: 'auto' }}
                                />
                            </div>
                            <Row $growContent>
                                <Col tabletUp={showLocation ? 1 / 2 : 1}>
                                    <EventDate
                                        startDate={startDate}
                                        endDate={endDate}
                                        size="large"
                                        style={{ marginTop: '20px' }}
                                    />
                                </Col>
                                {showLocation && (
                                    <Col
                                        tabletUp={1 / 2}
                                        style={{ marginTop: '20px' }}
                                    >
                                        <EventLocation
                                            entity={entity}
                                            size="normal"
                                        />
                                    </Col>
                                )}
                            </Row>

                            {source && <EventLink entity={entity} />}

                            {videoCallEnabled &&
                                videoCallUrl &&
                                integratedVideocallEnabled && (
                                    <Flexer
                                        justifyContent="flex-start"
                                        style={{ marginTop: 20 }}
                                    >
                                        <Button
                                            size="large"
                                            variant="secondary"
                                            as="a"
                                            href={videoCallUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <CameraIcon
                                                style={{
                                                    margin: '0 8px 0 -2px',
                                                }}
                                            />
                                            {t('global.video-call')}
                                            <HideVisually>
                                                {t(
                                                    'global.opens-in-new-window',
                                                )}
                                            </HideVisually>
                                        </Button>
                                    </Flexer>
                                )}

                            <TiptapView
                                content={translatedRichDescription}
                                style={{ marginTop: '24px' }}
                            />

                            {hasTranslations && (
                                <ToggleTranslation
                                    isTranslated={isTranslated}
                                    setIsTranslated={setIsTranslated}
                                    style={{ marginTop: '12px' }}
                                />
                            )}

                            <ItemTags
                                showCustomTags={showCustomTagsInDetail}
                                showTags={showTagsInDetail}
                                customTags={tags}
                                tagCategories={tagCategories}
                                style={{ marginTop: '24px' }}
                            />

                            {activitiesByDate?.length > 0 && (
                                <div style={{ marginTop: '24px' }}>
                                    {showActivityDateTabs && (
                                        <TabMenu
                                            label={t('entity-event.activities')}
                                            showBorder
                                            options={activitiesByDate.map(
                                                (activity, index) => {
                                                    const key =
                                                        Object.keys(activity)[0]
                                                    const hashIsKey =
                                                        hash === `#${key}`
                                                    const { guid } =
                                                        Object.values(
                                                            activity,
                                                        )[0][0]

                                                    return {
                                                        id: `tab-${guid}`,
                                                        label: showDate(
                                                            formatISO(key),
                                                            false,
                                                            true,
                                                        ),
                                                        link: `#${key}`,
                                                        isActive:
                                                            index === 0
                                                                ? !hash ||
                                                                  hashIsKey
                                                                : hashIsKey,
                                                        ariaControls: `tabpanel-${guid}`,
                                                    }
                                                },
                                            )}
                                            style={{ margin: '8px 0 16px' }}
                                        />
                                    )}

                                    <div>
                                        {activeHashActivities?.map(
                                            (activity, i) => {
                                                return (
                                                    <TabPanel
                                                        key={activity.guid}
                                                        id={`tabpanel-${activity.guid}`}
                                                        aria-labelledby={`tab-${activity.guid}`}
                                                        visible
                                                    >
                                                        <Activity
                                                            entity={activity}
                                                            showSlots
                                                            showSpots
                                                            isFirst={i === 0}
                                                            isLast={
                                                                i ===
                                                                activeHashActivities.length -
                                                                    1
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    paddingRight:
                                                                        '4px',
                                                                }}
                                                            >
                                                                <ActivityActions
                                                                    entity={
                                                                        activity
                                                                    }
                                                                    viewer={
                                                                        viewer
                                                                    }
                                                                    refetchQueries={
                                                                        refetchQueries
                                                                    }
                                                                    disabled={
                                                                        !canAttendSubEvents
                                                                    }
                                                                />
                                                            </div>
                                                        </Activity>
                                                    </TabPanel>
                                                )
                                            },
                                        )}
                                    </div>
                                </div>
                            )}

                            {isPublished &&
                                window.__SETTINGS__.enableSharing && (
                                    <SocialShare />
                                )}
                        </Col>
                        <Col mobileLandscape={1 / 2} tabletUp={1 / 3}>
                            <AttendeesActionsCard
                                entity={entity}
                                viewer={viewer}
                                site={site}
                            />
                        </Col>
                    </Row>
                </Container>

                <Container size="large">
                    <Row style={{ marginTop: '32px' }}>
                        <Col tabletUp={2 / 3}>
                            <H4 as="h2">
                                {t('comments.count-comments', {
                                    count: commentCount,
                                })}
                            </H4>
                            <CommentList
                                viewer={viewer}
                                entity={entity}
                                refetchQueries={refetchQueries}
                            />
                        </Col>
                        {hasSuggestedItems && (
                            <Col tabletUp={1 / 3}>
                                <ItemSuggestedItems entity={entity} />
                            </Col>
                        )}
                    </Row>
                </Container>
            </Section>

            <AttendeesModal
                tab={attendeesModal}
                setTab={setAttendeesModal}
                entity={entity}
            />

            <Modal
                size="normal"
                isVisible={showInvitePeopleModal}
                onClose={toggleInvitePeopleModal}
                title={t('global.invite-people')}
                showCloseButton
                containHeight
            >
                <InvitePeople guid={guid} onClose={toggleInvitePeopleModal} />
            </Modal>

            <Modal
                size="small"
                isVisible={showAddAttendeeModal}
                onClose={toggleAddAttendeeModal}
                title={t('entity-event.add-attendee')}
                showCloseButton
            >
                <AddAttendee guid={guid} onClose={toggleAddAttendeeModal} />
            </Modal>

            <Modal
                size="normal"
                title={t('global.send-message')}
                showCloseButton
                isVisible={showSendMessageModal}
                onClose={toggleSendMessageModal}
                containHeight
            >
                <SendMessage entity={entity} onClose={toggleSendMessageModal} />
            </Modal>

            {hasChildren && (
                <Modal
                    isVisible={showSlotsModal}
                    showCloseButton
                    onClose={toggleSlotsModal}
                    title={t('entity-event.activity-slots')}
                    size="normal"
                >
                    <SlotsForm entity={entity} onClose={toggleSlotsModal} />
                </Modal>
            )}
        </ThemeProvider>
    )
}

export default Item
